/** Bottom panel: the Problems list (live) and the animation timeline. */
import { AlertCircle, Info, TriangleAlert } from 'lucide-react';
import { t } from '../i18n.ts';
import type { Problem } from '../model/problems.ts';
import { useEditor, type EditorStore } from '../state/store.ts';
import { TimelinePanel } from '../timeline/TimelinePanel.tsx';
import { onTablistKeyDown, tabProps } from '../ui/controls.tsx';

function Icon({ p }: { p: Problem }) {
  if (p.severity === 'error') return <AlertCircle size={14} className="sev-error" aria-label={t('problems.error')} />;
  if (p.severity === 'warning') return <TriangleAlert size={14} className="sev-warning" aria-label={t('problems.warning')} />;
  return <Info size={14} className="sev-info" aria-label={t('problems.info')} />;
}

export function BottomPanel({ store, problems }: { store: EditorStore; problems: Problem[] }) {
  const tab = useEditor(store, (s) => s.ui.bottomTab);
  const counted = problems.filter((p) => p.severity !== 'info').length;
  return (
    <section className="bottom" aria-label={t('bottom.label')}>
      <div className="tabs" role="tablist" aria-label={t('bottom.label')} onKeyDown={onTablistKeyDown}>
        <button type="button" {...tabProps(tab === 'problems', 'bottom-panel')} id="bottom-tab-problems" className={tab === 'problems' ? 'active' : ''} onClick={() => store.setUi({ bottomTab: 'problems' })} data-testid="tab-problems">
          {t('bottom.problems')} {counted > 0 && <span className="count" data-testid="problem-count">{counted}</span>}
        </button>
        <button type="button" {...tabProps(tab === 'timeline', 'bottom-panel')} id="bottom-tab-timeline" className={tab === 'timeline' ? 'active' : ''} onClick={() => store.setUi({ bottomTab: 'timeline' })} data-testid="tab-timeline">
          {t('bottom.timeline')}
        </button>
      </div>
      {tab === 'problems' ? (
        <div className="problems-tab" role="tabpanel" id="bottom-panel" aria-labelledby="bottom-tab-problems">
        <ul className="problems" aria-live="polite" data-testid="problems-list">
          {problems.length === 0 && <li className="empty">{t('problems.none')}</li>}
          {problems.map((p) => (
            <li key={p.key} className={`problem sev-${p.severity}`} data-testid="problem" data-code={p.code}>
              <button
                type="button"
                onClick={() => {
                  if (p.elementId) store.select([p.elementId]);
                  if (p.fieldId) store.setUi({ rightTab: 'data' });
                }}
              >
                <Icon p={p} />
                <span className="problem-text">{t(p.code, p.params)}</span>
                {p.source && <span className="badge muted">{t(`problems.source.${p.source}`)}</span>}
              </button>
            </li>
          ))}
        </ul>
        </div>
      ) : (
        <div className="timeline-tab" role="tabpanel" id="bottom-panel" aria-labelledby="bottom-tab-timeline">
          <TimelinePanel store={store} />
        </div>
      )}
    </section>
  );
}
